import Decimal from 'decimal.js';
import { money, pct } from './money';
import type { WalletTopupBracket } from './tenant-features';

export type TopupBonusResult = {
  /** Bonus amount to credit (always > 0 when non-null). */
  bonus: Decimal;
  /** Wallet kind the bonus lands in — decided by the bracket's bonusKind. */
  bonusCurrency: 'cash' | 'gc';
  /** The matched bracket's stable id (recorded on the topup_requests row). */
  bracketId: string;
};

/**
 * Resolve EVERY bonus an approved wallet top-up earns. ALL brackets whose
 * [minAmount, maxAmount] contain `amount` apply (both bounds INCLUSIVE; null
 * maxAmount = open-ended top bracket) — so a "% to Wallet" bracket and a "% to
 * GC" bracket covering the same range BOTH credit, each landing in the wallet
 * kind named by its `bonusKind` (pct_wallet/fixed_wallet → cash, pct_gc/fixed_gc
 * → gc), independent of the kind being topped up. Percentages apply to the
 * principal `amount`. Non-positive bonuses are skipped. Returns [] when nothing
 * matches. Result order follows bracket order; the service writes one ledger
 * entry per result.
 *
 * Pure + Decimal-only so the admin/member UIs preview the exact same math the
 * service credits from (mirrors how `computePayoutDeductions` is shared).
 */
export function resolveTopupBonuses(
  brackets: WalletTopupBracket[],
  amount: Decimal,
): TopupBonusResult[] {
  if (amount.lte(0)) return [];
  const out: TopupBonusResult[] = [];
  for (const b of brackets) {
    if (amount.lt(money(b.minAmount))) continue;
    if (b.maxAmount != null && amount.gt(money(b.maxAmount))) continue;
    const isPct = b.bonusKind === 'pct_wallet' || b.bonusKind === 'pct_gc';
    const bonus = isPct ? pct(amount, money(b.value)) : money(b.value);
    if (bonus.lte(0)) continue;
    const bonusCurrency = b.bonusKind === 'pct_gc' || b.bonusKind === 'fixed_gc' ? 'gc' : 'cash';
    out.push({ bonus, bonusCurrency, bracketId: b.id });
  }
  return out;
}

/**
 * The gift check programs an approved top-up of `amount` earns (cashier phase
 * 5): one per matching bracket that names a program, each program once however
 * many brackets name it. Same range rule as `resolveTopupBonuses` (both bounds
 * inclusive, a null max is open-ended). Whether a program still exists, is
 * active and is earned by a top-up is the service's to check.
 */
export function resolveTopupVoucherPrograms(
  brackets: WalletTopupBracket[],
  amount: Decimal,
): string[] {
  if (amount.lte(0)) return [];
  const out: string[] = [];
  for (const b of brackets) {
    if (!b.voucherProgramId) continue;
    if (amount.lt(money(b.minAmount))) continue;
    if (b.maxAmount != null && amount.gt(money(b.maxAmount))) continue;
    if (!out.includes(b.voucherProgramId)) out.push(b.voucherProgramId);
  }
  return out;
}
